import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Navigation from '../components/Navigation'
import { useAuth } from '../hooks/useAuth'
import {
  apiClient,
  type AdminAuditPage,
  type AdminProductMetrics,
  type AdminUsersPage,
  type MetricPoint
} from '../services/apiClient'

type Tab = 'overview' | 'users' | 'audit'

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-firedup-gray/60 border border-firedup-light-gray rounded-lg p-5">
      <div className="text-sm uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-3xl font-black text-firedup-orange mt-2">{value.toLocaleString()}</div>
    </div>
  )
}

function Breakdown({ title, points }: { title: string; points: MetricPoint[] }) {
  const max = Math.max(...points.map(point => point.value), 1)
  return (
    <section className="bg-firedup-gray/50 border border-firedup-light-gray rounded-lg p-5">
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      <div className="space-y-3">
        {points.length === 0 && <p className="text-gray-400">No activity in this period.</p>}
        {points.map(point => (
          <div key={point.label}>
            <div className="flex justify-between text-sm mb-1">
              <span className="truncate mr-4">{point.label}</span>
              <span className="text-firedup-orange font-bold">{point.value.toLocaleString()}</span>
            </div>
            <div className="h-2 bg-firedup-dark rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-firedup-orange to-firedup-red"
                style={{ width: `${Math.max(3, (point.value / max) * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default function AdminConsolePage() {
  const { user } = useAuth()
  const [tab, setTab] = useState<Tab>('overview')
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setUTCDate(date.getUTCDate() - 29)
    return isoDate(date)
  })
  const [endDate, setEndDate] = useState(() => isoDate(new Date()))
  const [metrics, setMetrics] = useState<AdminProductMetrics | null>(null)
  const [users, setUsers] = useState<AdminUsersPage | null>(null)
  const [audit, setAudit] = useState<AdminAuditPage | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [role, setRole] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [appliedStatus, setAppliedStatus] = useState('')
  const [appliedRole, setAppliedRole] = useState('')
  const [userPage, setUserPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [actionUserId, setActionUserId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const userRequestId = useRef(0)
  const metricsRequestId = useRef(0)
  const loadRequestId = useRef(0)

  const loadMetrics = useCallback(async () => {
    const requestId = ++metricsRequestId.current
    const result = await apiClient.getAdminMetrics(startDate, endDate)
    if (requestId === metricsRequestId.current) {
      setMetrics(result)
    }
  }, [startDate, endDate])

  const loadUsers = useCallback(async () => {
    const requestId = ++userRequestId.current
    const result = await apiClient.getAdminUsers({
      search: appliedSearch || undefined,
      status: appliedStatus || undefined,
      role: appliedRole || undefined,
      page: userPage,
      pageSize: 25
    })
    if (requestId === userRequestId.current) {
      setUsers(result)
    }
  }, [appliedRole, appliedSearch, appliedStatus, userPage])

  const loadAudit = useCallback(async () => {
    setAudit(await apiClient.getAdminAuditLog())
  }, [])

  useEffect(() => {
    const requestId = ++loadRequestId.current
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        await Promise.all([loadMetrics(), loadUsers(), loadAudit()])
      } catch (err) {
        if (requestId === loadRequestId.current) {
          setError(err instanceof Error ? err.message : 'Failed to load the admin console')
        }
      } finally {
        if (requestId === loadRequestId.current) {
          setLoading(false)
        }
      }
    }
    load()
  }, [loadAudit, loadMetrics, loadUsers])

  const totalUserPages = useMemo(
    () => Math.max(1, Math.ceil((users?.total || 0) / (users?.pageSize || 25))),
    [users]
  )

  const submitUserSearch = (event: FormEvent) => {
    event.preventDefault()
    setUserPage(1)
    setAppliedSearch(search)
    setAppliedStatus(status)
    setAppliedRole(role)
  }

  const updateRole = async (userId: number, enabled: boolean) => {
    const verb = enabled ? 'grant administrator access to' : 'remove administrator access from'
    if (!window.confirm(`Are you sure you want to ${verb} this user?`)) return
    setActionUserId(userId)
    setError(null)
    try {
      await apiClient.setAdminRole(userId, enabled)
      await Promise.all([loadUsers(), loadAudit()])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Role update failed')
    } finally {
      setActionUserId(null)
    }
  }

  const updateStatus = async (userId: number, nextStatus: 'active' | 'suspended') => {
    if (!window.confirm(`Are you sure you want to mark this account ${nextStatus}?`)) return
    setActionUserId(userId)
    setError(null)
    try {
      await apiClient.setAdminUserStatus(userId, nextStatus)
      await Promise.all([loadUsers(), loadAudit(), loadMetrics()])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status update failed')
    } finally {
      setActionUserId(null)
    }
  }

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-firedup-orange font-bold uppercase tracking-wider">Administrator</p>
            <h1 className="text-4xl font-black">Operations Console</h1>
            <p className="text-gray-400 mt-2">Manage users and monitor product activity.</p>
          </div>
          <div className="flex gap-2" role="tablist" aria-label="Admin sections">
            {(['overview', 'users', 'audit'] as Tab[]).map(item => (
              <button
                key={item}
                onClick={() => setTab(item)}
                className={tab === item ? 'btn-primary capitalize' : 'btn-secondary capitalize'}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 border border-red-500 bg-red-950/50 text-red-200 rounded-lg p-4">{error}</div>
        )}
        {loading && <div className="text-firedup-orange text-xl">Loading admin data...</div>}

        {!loading && tab === 'overview' && metrics && (
          <div className="space-y-6">
            <form
              className="flex flex-wrap items-end gap-3 bg-firedup-gray/50 border border-firedup-light-gray rounded-lg p-4"
              onSubmit={event => {
                event.preventDefault()
                loadMetrics().catch(err => setError(err instanceof Error ? err.message : 'Metrics failed'))
              }}
            >
              <label className="text-sm text-gray-300">
                Start date
                <input className="input-field mt-1" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </label>
              <label className="text-sm text-gray-300">
                End date
                <input className="input-field mt-1" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </label>
              <button className="btn-secondary" type="submit">Apply range</button>
            </form>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="Total users" value={metrics.totalUsers} />
              <MetricCard label="New users" value={metrics.newUsers} />
              <MetricCard label="Verified users" value={metrics.verifiedUsers} />
              <MetricCard label="Active players" value={metrics.activeUsers} />
              <MetricCard label="Matches" value={metrics.totalMatches} />
              <MetricCard label="Scores" value={metrics.totalScores} />
              <MetricCard label="Claimed scores" value={metrics.claimedScores} />
              <MetricCard label="Anonymous scores" value={metrics.anonymousScores} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Breakdown title="Matches by day" points={metrics.matchesByDay} />
              <Breakdown title="Registrations by day" points={metrics.registrationsByDay} />
              <Breakdown title="Top games" points={metrics.matchesByGame} />
              <Breakdown title="Top locations" points={metrics.matchesByLocation} />
              <Breakdown title="Platforms" points={metrics.matchesByPlatform} />
            </div>
          </div>
        )}

        {!loading && tab === 'users' && (
          <div className="space-y-5">
            <form className="grid md:grid-cols-4 gap-3" onSubmit={submitUserSearch}>
              <input
                className="input-field"
                placeholder="Search name or email"
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
              <select className="input-field" value={status} onChange={event => setStatus(event.target.value)}>
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <select className="input-field" value={role} onChange={event => setRole(event.target.value)}>
                <option value="">All roles</option>
                <option value="admin">Administrators</option>
              </select>
              <button className="btn-secondary" type="submit">Search</button>
            </form>

            <div className="overflow-x-auto border border-firedup-light-gray rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-firedup-gray text-gray-300">
                  <tr>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Activity</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-firedup-light-gray">
                  {users?.users.map(managedUser => {
                    const isAdmin = managedUser.roles.includes('admin')
                    const isSelf = managedUser.id === user?.id
                    return (
                      <tr key={managedUser.id} className="bg-firedup-dark/40">
                        <td className="p-3">
                          <div className="font-bold">{managedUser.displayName || `${managedUser.firstName} ${managedUser.lastName}`}</div>
                          <div className="text-gray-400">{managedUser.email}</div>
                          {isAdmin && <span className="text-xs text-firedup-orange font-bold">ADMIN</span>}
                        </td>
                        <td className="p-3">
                          <span className={managedUser.accountStatus === 'active' ? 'text-green-400' : 'text-red-400'}>
                            {managedUser.accountStatus}
                          </span>
                          <div className="text-xs text-gray-500">{managedUser.emailVerified ? 'Verified' : 'Unverified'}</div>
                        </td>
                        <td className="p-3 text-gray-300">
                          <div>{managedUser.totalMatches} matches / {managedUser.totalScores} scores</div>
                          <div className="text-xs text-gray-500">
                            Last active: {managedUser.lastActiveAt ? new Date(managedUser.lastActiveAt).toLocaleDateString() : 'Never'}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-2">
                            <button
                              className="btn-secondary text-xs"
                              disabled={isSelf || actionUserId === managedUser.id}
                              onClick={() => updateRole(managedUser.id, !isAdmin)}
                            >
                              {isAdmin ? 'Remove admin' : 'Make admin'}
                            </button>
                            <button
                              className="btn-secondary text-xs"
                              disabled={isSelf || actionUserId === managedUser.id}
                              onClick={() => updateStatus(
                                managedUser.id,
                                managedUser.accountStatus === 'active' ? 'suspended' : 'active'
                              )}
                            >
                              {managedUser.accountStatus === 'active' ? 'Suspend' : 'Reactivate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">{users?.total || 0} users</span>
              <div className="flex items-center gap-3">
                <button className="btn-secondary" disabled={userPage <= 1} onClick={() => setUserPage(page => page - 1)}>Previous</button>
                <span>Page {userPage} of {totalUserPages}</span>
                <button className="btn-secondary" disabled={userPage >= totalUserPages} onClick={() => setUserPage(page => page + 1)}>Next</button>
              </div>
            </div>
          </div>
        )}

        {!loading && tab === 'audit' && (
          <div className="border border-firedup-light-gray rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-firedup-gray">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Administrator</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Target</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-firedup-light-gray">
                {audit?.entries.map(entry => (
                  <tr key={entry.id}>
                    <td className="p-3 text-gray-400">{new Date(entry.createdAt).toLocaleString()}</td>
                    <td className="p-3">{entry.actorEmail}</td>
                    <td className="p-3 text-firedup-orange">{entry.action}</td>
                    <td className="p-3">{entry.targetEmail || 'System'}</td>
                  </tr>
                ))}
                {audit?.entries.length === 0 && (
                  <tr><td className="p-6 text-gray-400 text-center" colSpan={4}>No administrative actions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
